import React, { useCallback, useEffect, useRef, useState } from 'react';
import Modal from '../ui/Modal';
import Button from '../ui/Button';
import ProviderNotice from './ProviderNotice';
import {
  RsvpReminderError,
  loadRsvpReminderPreview,
  mapRsvpReminderError,
  sendRsvpReminder,
} from '../../services/RsvpReminderService';
import type { RsvpReminderPreview, RsvpReminderResult } from '../../types/RsvpReminder';

interface RsvpReminderModalProps {
  open: boolean;
  onClose: () => void;
  /** Called after a send so the inbox can reload and show the messages that were logged. */
  onSent?: () => void;
}

type Phase = 'form' | 'confirm' | 'sending' | 'done';

const plural = (n: number, one: string, many: string): string => `${n} ${n === 1 ? one : many}`;

/**
 * The RSVP deadline reminder: type the deadline, see exactly who would be reminded (and who is left out, and why),
 * read the message as they will, then confirm. Sending is always manual and always on the list that was previewed:
 * changing the deadline discards the preview, and the server refuses a send whose list has moved.
 */
const RsvpReminderModal: React.FC<RsvpReminderModalProps> = ({ open, onClose, onSent }) => {
  const [deadline, setDeadline] = useState('');
  const [preview, setPreview] = useState<RsvpReminderPreview | null>(null);
  const [previewedFor, setPreviewedFor] = useState('');
  const [loading, setLoading] = useState(false);
  const [phase, setPhase] = useState<Phase>('form');
  const [result, setResult] = useState<RsvpReminderResult | null>(null);
  const [error, setError] = useState('');
  const abort = useRef<AbortController | null>(null);

  useEffect(() => {
    if (open) {
      setDeadline(''); setPreview(null); setPreviewedFor(''); setLoading(false);
      setPhase('form'); setResult(null); setError('');
    }
    return () => abort.current?.abort();
  }, [open]);

  const trimmed = deadline.trim();
  const fresh = preview !== null && previewedFor === trimmed;

  const loadPreview = useCallback(async (forDeadline: string) => {
    abort.current?.abort();
    const controller = new AbortController();
    abort.current = controller;
    setLoading(true);
    try {
      const next = await loadRsvpReminderPreview(forDeadline, controller.signal);
      if (controller.signal.aborted) return;
      setPreview(next);
      setPreviewedFor(forDeadline);
    } catch (err) {
      if (controller.signal.aborted) return;
      setPreview(null);
      setError(mapRsvpReminderError(err).message);
    } finally {
      if (!controller.signal.aborted) setLoading(false);
    }
  }, []);

  const showPreview = async () => {
    if (!trimmed) return;
    setError('');
    await loadPreview(trimmed);
  };

  const send = async () => {
    if (!fresh || !preview) return;
    setPhase('sending');
    setError('');
    try {
      setResult(await sendRsvpReminder(previewedFor, preview.batchToken));
      setPhase('done');
      onSent?.();
    } catch (err) {
      const mapped = mapRsvpReminderError(err);
      setError(mapped.message);
      setPhase('form');
      // The list moved under the screen: show the new one so the person decides again.
      if (mapped instanceof RsvpReminderError && mapped.listChanged) await loadPreview(previewedFor);
    }
  };

  const canSend = fresh && preview!.configured && preview!.willSend > 0;

  const footer = phase === 'done' ? (
    <Button variant="primary" onClick={onClose}>Close</Button>
  ) : phase === 'confirm' ? (
    <>
      <Button variant="secondary" onClick={() => setPhase('form')}>Back</Button>
      <Button variant="primary" onClick={send}>Yes, send now</Button>
    </>
  ) : (
    <>
      <Button variant="secondary" onClick={onClose}>Close</Button>
      {canSend && phase === 'form' && (
        <Button variant="primary" onClick={() => setPhase('confirm')}>
          {`Send to ${plural(preview!.willSend, 'guest', 'guests')}`}
        </Button>
      )}
    </>
  );

  return (
    <Modal open={open} onClose={onClose} title="RSVP reminder" size="medium" footer={footer}>
      <div className="communications-compose rsvp-reminder">
        {error && <div className="communications-error" role="alert">{error}</div>}

        {phase === 'done' && result && (
          <div role="status" className="rsvp-reminder-result">
            <p><strong>{`${result.sent} sent`}</strong>{result.failed > 0 && <>, <strong>{`${result.failed} failed`}</strong></>}.</p>
            {result.stoppedEarly && (
              <p className="communications-hint">
                Sending stopped after repeated failures. Check the WhatsApp settings, then try again.
              </p>
            )}
            {result.remaining > 0 && (
              <p className="communications-hint">
                {result.stoppedEarly ? `${result.remaining} not attempted.` : `${result.remaining} more will go in the next send.`}
              </p>
            )}
            <p className="communications-hint">Each message is in the inbox, with its delivery status.</p>
          </div>
        )}

        {phase === 'confirm' && preview && (
          <p role="alert" className="rsvp-reminder-confirm">
            {`Send ${plural(preview.willSend, 'WhatsApp message', 'WhatsApp messages')} now? This cannot be undone.`}
          </p>
        )}

        {(phase === 'form' || phase === 'sending') && (
          <>
            <p className="communications-hint">
              Reminds guests who are invited but have not replied to anything. Nothing is sent until you confirm.
            </p>
            <div className="form-group">
              <label className="form-label" htmlFor="rsvp-reminder-deadline">RSVP closes on</label>
              <input
                id="rsvp-reminder-deadline" name="deadline" type="text" className="form-control" value={deadline}
                maxLength={40} placeholder="e.g. 4 November" autoComplete="off" disabled={phase === 'sending'}
                onChange={e => setDeadline(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); void showPreview(); } }}
              />
            </div>
            <div>
              <Button variant="secondary" onClick={showPreview} loading={loading} disabled={!trimmed || phase === 'sending'}>
                Show who will be reminded
              </Button>
            </div>

            {fresh && preview && (
              <div className="rsvp-reminder-preview">
                {!preview.configured && (
                  preview.notConfiguredReason === 'provider_not_configured'
                    ? <ProviderNotice />
                    : <p className="communications-provider-notice" role="status">
                        The reminder message template has not been approved and added yet. Ask an admin to set it up before sending.
                      </p>
                )}
                {preview.eligible === 0 ? (
                  <p><strong>Nobody needs a reminder right now.</strong></p>
                ) : (
                  <>
                    <p><strong>{`${plural(preview.eligible, 'guest', 'guests')} will be sent a reminder`}</strong></p>
                    {preview.eligible > preview.willSend && (
                      <p className="communications-hint">
                        {`Sending to the first ${preview.willSend} of ${preview.eligible}. ${preview.eligible - preview.willSend} will wait for the next send.`}
                      </p>
                    )}
                    {preview.sample && <blockquote className="rsvp-reminder-sample">{preview.sample}</blockquote>}
                  </>
                )}
                <ExcludedList excluded={preview.excluded} />
              </div>
            )}
          </>
        )}
      </div>
    </Modal>
  );
};

const ExcludedList: React.FC<{ excluded: RsvpReminderPreview['excluded'] }> = ({ excluded }) => {
  const lines: string[] = [];
  if (excluded.replied > 0) lines.push(`${excluded.replied} ${excluded.replied === 1 ? 'has' : 'have'} already replied`);
  if (excluded.notInvited > 0) lines.push(`${excluded.notInvited} ${excluded.notInvited === 1 ? 'is' : 'are'} not invited to anything yet`);
  if (excluded.noPhone > 0) lines.push(`${excluded.noPhone} ${excluded.noPhone === 1 ? 'has' : 'have'} no usable phone number`);
  if (excluded.recentlyReminded > 0) lines.push(`${excluded.recentlyReminded} ${excluded.recentlyReminded === 1 ? 'was' : 'were'} reminded in the last 7 days`);
  if (lines.length === 0) return null;
  return (
    <div>
      <p className="communications-hint">Left out:</p>
      <ul className="rsvp-reminder-excluded">{lines.map(l => <li key={l}>{l}</li>)}</ul>
    </div>
  );
};

export default RsvpReminderModal;
